import {
  useState,
  useCallback,
  useEffect,
  createContext,
  useContext,
} from "react";
import * as authApi from "src/api/auth";
import { getUserTokenBalance } from "src/api/tokenTransaction";

/**
 * Authentication context containing user data and utilities
 * @typedef {Object} Auth - Auth utilities and state containing:
 * @property {Function} logout - Function to clear authentication state
 * @property {Function} setToken - Updates JWT token in state and storage
 * @property {Function} me - Async function to fetch current user data
 * @property {Function} setUser - Updates user state manually
 * @property {Object|null} user - Current user data or null
 * @property {Object|null} userPermission - Current user permission data or null
 * @property {boolean} isLoggedIn - Authentication status
 * @property {string} token - Current JWT token
 */

/**
 * Custom hook for using authentication context
 * @returns {Auth} Auth utilities and state
 */
export function useAuth() {
  const context = useContext(AuthContext);
  return context;
}

const AuthContext = createContext();

export class ApiError extends Error {
  constructor(params = {}) {
    const { statusCode, message } = params;
    super(message);
    this.statusCode = statusCode;
    this.message = message;
  }
}

export function AuthProvider({ children }) {
  const [token, setTokenState] = useState(() => {
    if (typeof window === "undefined") return "";
    return localStorage.getItem("token") || "";
  });
  const [user, setUser] = useState(() => {
    if (typeof window === "undefined") return null;
    const userData = localStorage.getItem("user");
    return userData ? JSON.parse(userData) : null;
  });
  const [userPermission, setUserPermission] = useState(() => {
    if (typeof window === "undefined") return null;
    const userData = localStorage.getItem("user");
    return userData ? JSON.parse(userData)?.admin_permission?.permission : null;
  });
  const [userTokenBalance, setUserTokenBalance] = useState({
    active_wallets_count: 0,
    current_balance: 0,
    expired_balance: 0,
    total_topup: 0,
    total_used: 0,
    total_wallets_count: 0,
  });

  const isLoggedIn = !!user;

  /**
   * Updates JWT token in state and localStorage
   * @param {string} newToken - JWT token string
   */
  const setToken = useCallback((newToken) => {
    localStorage.setItem("token", newToken);
    setTokenState(newToken);
  }, []);

  /**
   * Check admin permission for action
   * @param {string} permPath - part of permission ex. "products.can_view"
   */
  const checkAllowPermission = useCallback(
    (permPath) => {
      const [group, perm] = permPath.split(".");
      return user?.role_id && userPermission
        ? userPermission[group]?.[perm] === true
        : true;
    },
    [user?.role_id, userPermission],
  );

  /**
   * 
   * @note Only for seller users, fetches token balance and updates state
    with the latest balance data from the server.
   * @returns {Promise<void>} Resolves when balance is updated
   * @throws {Error} Throws error if request fails
   */
  const checkGenerateTokenBalance = useCallback(async () => {
    if (user?.role_id === 1) return;

    try {
      const data = await getUserTokenBalance();
      setUserTokenBalance(data);
    } catch (error) {
      console.error("Error checking token generation balance:", error);
    }
  }, [user?.role_id]);

  /**
   * Handles user login authentication
   * @param {string} provider - OAuth provider name
   * @param {Object} credentials - User login credentials
   * @param {number} credentials.login_type - Login type (4: user/admin, 3: seller)
   * @param {string} credentials.email - User's email
   * @param {string} credentials.password - User's password
   * @param {string} credentials.token - OAuth token
   * @returns {Promise<void>} Resolves on successful login
   * @throws {Error} Throws error with server message or default
   */
  const login = useCallback(
    async (credentials) => {
      try {
        const data = await authApi.login(credentials);
        const { token: newToken } = data;
        setToken(newToken);
      } catch (error) {
        const message = error.response?.data?.message || "Login failed";
        throw new ApiError({
          statusCode: error.response.status,
          message,
        });
      }
    },
    [setToken],
  );

  /**
   * Clears authentication state and token
   */
  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setTokenState("");
    setUser(null);
    setUserPermission(null);
    localStorage.clear();
    if (typeof window !== "undefined") window.location.href = "/";
  }, []);

  /**
   * Fetches current user data using JWT token
   * @returns {Promise<void>} Resolves with user data
   * @throws {Error} Throws error if no token or request fails
   */
  const me = useCallback(async () => {
    try {
      const data = await authApi.getUserInfo();
      setUser(data);
      setUserPermission(data?.admin_permission?.permission || null);
      localStorage.setItem("user", JSON.stringify(data));
    } catch (error) {
      const message = error.response?.data?.message || "Failed to fetch user";
      throw new ApiError({
        statusCode: error.response.status,
        message,
      });
    }
  }, []);

  // Automatically fetch user when token changes or on mount
  useEffect(() => {
    if (token) {
      me().catch((error) => {
        console.error("User info fetch failed:", error);
        logout();
      });
    }
  }, [token, me, logout]);

  const auth = {
    setToken,
    login,
    logout,
    setUser,
    user,
    isLoggedIn,
    token,
    me,
    userPermission,
    checkAllowPermission,
    userTokenBalance,
    checkGenerateTokenBalance,
  };

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}
